import getMobiusResponse from '@/utils/mobius/getMobiusResponse';
import { MobiusProgramResponse, MobiusTrackingContext } from '@/types/Mobius';
import { Program, Occupation, FormState } from '../../SalaryCalculator/types';
import getAliasParams from '@/utils/getAliasParams';
import getUrlQuery from '@/utils/getUrlQuery';
import getTrafficSource from '@/utils/getTrafficSource';
import uuid from '@/utils/uuid';

export interface FetchProgramsParams {
  selectedDegree: string;
  selectedCategory: string;
  selectedSubject: string;
  formStateOptions: FormState['options'];
  identityToken: string;
  trackingContext: MobiusTrackingContext;
  domain?: string;
  presets?: Record<string, any>;
}

export interface FetchProgramsResult {
  programs: Program[];
  occupationCodes: string[];
}

// Helper function to get numeric ID from form options
export const getNumericId = (
  field: string,
  slug: string,
  options: FormState['options'],
) => {
  if (!options[field as keyof typeof options]) return 0;

  const option = options[field as keyof typeof options]?.find(
    (opt) => opt.slug === slug,
  );
  return option?.id || 0;
};

// Helper function to get optimal salary distribution
export const getOptimalSalaryDistribution = (
  data: Occupation[],
): Occupation[] => {
  if (data.length <= 6) {
    return data;
  }

  const result: Occupation[] = [];
  const length = data.length;

  // Add lowest 2
  result.push(data[0], data[1]);

  // Add middle 2
  const midIndex = Math.floor(length / 2);
  if (length % 2 === 0) {
    result.push(data[midIndex - 1], data[midIndex]);
  } else {
    result.push(data[midIndex - 1], data[midIndex]);
  }

  // Add highest 2
  result.push(data[length - 2], data[length - 1]);

  return result;
};

/**
 * Fetch programs from Mobius API based on DCS selections
 */
export const fetchPrograms = async ({
  selectedDegree,
  selectedCategory,
  selectedSubject,
  formStateOptions,
  identityToken,
  trackingContext,
  domain,
  presets = {},
}: FetchProgramsParams): Promise<FetchProgramsResult> => {
  // Validate all selections are present
  if (!selectedSubject || !selectedCategory || !selectedDegree) {
    return { programs: [], occupationCodes: [] };
  }

  if (!formStateOptions) {
    throw new Error('Form state options are required');
  }

  const subjectId = getNumericId('subject', selectedSubject, formStateOptions);
  const categoryId = getNumericId(
    'category',
    selectedCategory,
    formStateOptions,
  );
  const degreeId = getNumericId('degree', selectedDegree, formStateOptions);

  // Only proceed if all IDs are valid
  if (!subjectId || !categoryId || !degreeId) {
    console.warn('Invalid D/C/S IDs (0) found, skipping Mobius call');
    return { programs: [], occupationCodes: [] };
  }

  const response = (await getMobiusResponse('criteriabased', identityToken, {
    criteria: {
      subjectId,
      categoryId,
      degreeId,
      limit: 3,
    },
  })) as MobiusProgramResponse;

  if (!response || response.length === 0) {
    return { programs: [], occupationCodes: [] };
  }

  // Process programs more efficiently
  const mappedPrograms: Program[] = response.map(
    (product: any, index: number) => {
      const correlationId = uuid();
      const programUrl = getUrlQuery(product?.product?.destination?.url || '', {
        publisher: domain || '',
        correlationId,
        idToken: identityToken || '',
        helid: product?.product?.destination?.helid || '',
        position: index + 1,
        source: getTrafficSource(
          typeof window !== 'undefined' ? window.location.href : '',
        ),
        trackingContext: JSON.stringify(trackingContext),
        ...presets,
        ...getAliasParams(),
      });

      return {
        id: product?.product?.program.id,
        name: product?.product?.program.name,
        description:
          product?.product?.program?.snippet ||
          product?.product?.program?.description ||
          product?.product?.school?.snippet ||
          product?.product?.school?.description,
        image: product?.image,
        schoolName:
          product?.product?.school?.name || product?.school_name || '',
        degreeLevel:
          product?.product?.program?.degree?.name ||
          product?.degree_level ||
          '',
        url: product?.product?.destination?.url,
        destinationUrl: programUrl,
        schoolSlug: product?.product?.school?.slug || '',
        capId: product?.metadata?.capId
          ? product?.metadata?.capId.toString()
          : '',
        requestId: product?.requestId || '',
        helid: product?.product?.destination.helid || '',
        related: product?.related || false,
      };
    },
  );

  // Collect unique occupation codes more efficiently
  const allOccupationCodes = new Set<string>();
  response.forEach((product: any) => {
    product.product?.program?.occupationCodes?.forEach((code: string) => {
      if (code && code.trim()) {
        allOccupationCodes.add(code);
      }
    });
  });

  const occupationCodesArray = Array.from(allOccupationCodes);

  return {
    programs: mappedPrograms,
    occupationCodes: occupationCodesArray,
  };
};

/**
 * Fetch occupations from the occupations service
 */
export const fetchOccupations = async (
  occupationCodes: string[],
): Promise<Occupation[]> => {
  if (!occupationCodes.length) return [];

  // Remove duplicates and filter empty codes
  const uniqueCodes = Array.from(
    new Set(occupationCodes.filter((code) => code && code.trim())),
  );

  // Limit to a reasonable number to avoid overwhelming the API
  const limitedCodes = uniqueCodes.slice(0, 6);

  const proxy = process.env.NEXT_PUBLIC_PROXY_OCCUPATIONS_SERVICE || '';
  const endpoint = `${proxy}/occupations`;

  // Process with concurrency limit (max 3 concurrent requests)
  const fetchSingleOccupation = async (
    occupation: string,
  ): Promise<Occupation | null> => {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          include: ['employmentAndWageStatistics', 'projections'],
          filter: {
            onetSocCode: occupation,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Only return data if it has average salary information
      if (data.data?.[0]?.employmentAndWageStatistics?.meanAnnualWage) {
        const result: Occupation = {
          description: data.data[0].description || '',
          title: data.data[0].title || '',
          onetSocCode: data.data[0].onetSocCode,
          salary: data.data[0].employmentAndWageStatistics.meanAnnualWage,
          employmentChange: data.data[0].projections?.employmentChange2023,
        };

        return result;
      }

      return null;
    } catch (err) {
      console.error(`Error fetching occupation ${occupation}:`, err);
      return null;
    }
  };

  // Process codes with batching (3 concurrent requests at a time)
  const results: (Occupation | null)[] = [];
  for (let i = 0; i < limitedCodes.length; i += 3) {
    const batch = limitedCodes.slice(i, i + 3);
    const batchResults = await Promise.allSettled(
      batch.map(fetchSingleOccupation),
    );
    batchResults.forEach((result) => {
      if (result.status === 'fulfilled') {
        results.push(result.value);
      }
    });
  }

  // Filter out null results and zero salaries, then sort
  const validOccupationData = results
    .filter(
      (result): result is Occupation => result !== null && result.salary > 0,
    )
    .sort((a, b) => a.salary - b.salary);

  // Optimize salary processing - use a more efficient algorithm
  return getOptimalSalaryDistribution(validOccupationData);
};
